'use client';

import { useState } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type Experience = '0-2' | '3-5' | '6-10' | '10+';
type Industry = 'tech' | 'finance' | 'healthcare' | 'government' | 'creative' | 'other';
type Level = 'entry' | 'mid' | 'senior' | 'executive' | 'career-change';
type Positions = '1-2' | '3-5' | '6+';

interface Answers {
  experience: Experience | null;
  industry: Industry | null;
  level: Level | null;
  positions: Positions | null;
}

type Recommendation = '1 Page' | '2 Pages' | '1-2 Pages (It Depends)';

/* ------------------------------------------------------------------ */
/*  Questions data                                                     */
/* ------------------------------------------------------------------ */

const questions = [
  {
    key: 'experience' as const,
    question: 'How many years of relevant work experience do you have?',
    options: [
      { label: '0-2 years', value: '0-2' as Experience },
      { label: '3-5 years', value: '3-5' as Experience },
      { label: '6-10 years', value: '6-10' as Experience },
      { label: '10+ years', value: '10+' as Experience },
    ],
  },
  {
    key: 'industry' as const,
    question: 'What industry are you in?',
    options: [
      { label: 'Tech / Software', value: 'tech' as Industry },
      { label: 'Finance / Consulting', value: 'finance' as Industry },
      { label: 'Healthcare / Science', value: 'healthcare' as Industry },
      { label: 'Government / Academic', value: 'government' as Industry },
      { label: 'Creative / Marketing', value: 'creative' as Industry },
      { label: 'Other', value: 'other' as Industry },
    ],
  },
  {
    key: 'level' as const,
    question: 'What level are you targeting?',
    options: [
      { label: 'Entry-Level', value: 'entry' as Level },
      { label: 'Mid-Career', value: 'mid' as Level },
      { label: 'Senior / Manager', value: 'senior' as Level },
      { label: 'Executive / Director', value: 'executive' as Level },
      { label: 'Career Change', value: 'career-change' as Level },
    ],
  },
  {
    key: 'positions' as const,
    question: 'How many relevant positions have you held?',
    options: [
      { label: '1-2 positions', value: '1-2' as Positions },
      { label: '3-5 positions', value: '3-5' as Positions },
      { label: '6+ positions', value: '6+' as Positions },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Decision logic                                                     */
/* ------------------------------------------------------------------ */

function getRecommendation(answers: Answers): {
  recommendation: Recommendation;
  explanation: string;
  tips: string[];
  exceptions: string;
} {
  const { experience, industry, level, positions } = answers;

  // Career change always => 1 page
  if (level === 'career-change') {
    return {
      recommendation: '1 Page',
      explanation:
        'As a career changer, a concise one-page resume is your best bet. Hiring managers want to quickly see how your transferable skills map to the new role. A focused, single-page format forces you to highlight only the most relevant experience and keeps the reader engaged.',
      tips: [
        'Lead with a strong summary that frames your career pivot clearly.',
        'Focus on transferable skills and measurable achievements, not job duties.',
        'Remove outdated or unrelated positions to keep things tight.',
        'Consider a skills-based (functional) or hybrid resume format.',
      ],
      exceptions:
        'If you have 10+ years of highly relevant cross-industry experience, a second page may be warranted. Some government or academic roles may also expect longer formats even for career changers.',
    };
  }

  // Government / Academic => 1-2 pages
  if (industry === 'government') {
    return {
      recommendation: '1-2 Pages (It Depends)',
      explanation:
        'Government and academic roles often expect more detail than private-sector positions. Federal resumes, for example, can be 2-5 pages. Academic CVs have no page limit. For a standard government or academic job application, 1-2 pages is typical, but always check the specific posting requirements.',
      tips: [
        'For federal jobs (USAJobs), follow the specific resume format guidelines -- longer is expected.',
        'Academic CVs should include publications, grants, and teaching experience without page limits.',
        'For state/local government, 1-2 pages is usually ideal.',
        'Include relevant certifications, clearances, and classification details if applicable.',
      ],
      exceptions:
        'Federal resumes and academic CVs are notable exceptions to the "keep it short" rule. Some postings explicitly state their preferred format. Always read the job announcement carefully.',
    };
  }

  // 10+ years OR senior/executive => 2 pages
  if (experience === '10+' || level === 'senior' || level === 'executive') {
    return {
      recommendation: '2 Pages',
      explanation:
        `With ${experience === '10+' ? 'over a decade of experience' : 'a senior-level target role'}, you have enough substantive content to justify two pages. Recruiters and hiring managers expect experienced professionals to have more to share. The key is making sure every line earns its place -- no filler.`,
      tips: [
        'Use page one for your most impactful and recent experience.',
        'Quantify achievements wherever possible (revenue, team size, efficiency gains).',
        'Older roles (10+ years ago) can be condensed to one or two lines each.',
        'Include a strong executive summary at the top that frames your career narrative.',
      ],
      exceptions:
        'Some tech companies and startups still prefer concise one-page resumes, even for senior hires. If the job posting emphasizes brevity or the company culture is lean, consider trimming to one page.',
    };
  }

  // 0-2 or 3-5 years AND entry/mid => 1 page
  if (
    (experience === '0-2' || experience === '3-5') &&
    (level === 'entry' || level === 'mid')
  ) {
    return {
      recommendation: '1 Page',
      explanation:
        'With your experience level, a one-page resume is the clear choice. It shows you can communicate concisely and prioritize what matters most. Recruiters reviewing early- and mid-career candidates spend an average of 7 seconds on the first scan -- a tight one-pager makes every second count.',
      tips: [
        'Focus on your most recent and relevant roles -- skip high school jobs or unrelated gigs.',
        'Use strong action verbs and quantify results (e.g., "Increased sales 30%").',
        'Keep your education section brief unless you recently graduated.',
        'Remove "References available upon request" -- it wastes space.',
      ],
      exceptions:
        'If you have extensive project work, publications, or certifications directly relevant to the role, a second page might be warranted. But for most positions at this stage, one page is ideal.',
    };
  }

  // 6-10 years, mid-career, 3-5 positions => It Depends (catch-all for mid-range)
  return {
    recommendation: '1-2 Pages (It Depends)',
    explanation:
      'You are in the range where both one and two pages can work well. The right choice depends on how much substantive, relevant content you have. If every bullet point is a strong achievement tied to the target role, two pages is fine. If you would need to add filler to reach two pages, stick with one.',
    tips: [
      'Draft a two-page version first, then ruthlessly edit -- can you cut it to one strong page?',
      'If your second page is less than half full, consolidate to one page.',
      'Prioritize the last 10 years of experience and reduce older roles to brief entries.',
      'Tailor your resume for each application -- some roles may need more detail than others.',
    ],
    exceptions:
      'If applying to a company known for wanting concise resumes (many startups and tech firms), lean toward one page. For roles that emphasize depth of experience (consulting, healthcare leadership), two pages can give you an edge.',
  };
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function LengthAdvisorContent() {
  const [step, setStep] = useState(0); // 0-3 = questions, 4 = results
  const [answers, setAnswers] = useState<Answers>({
    experience: null,
    industry: null,
    level: null,
    positions: null,
  });
  const [transitioning, setTransitioning] = useState(false);

  const totalSteps = 4;
  const isComplete = step >= totalSteps;

  function handleSelect(key: string, value: string) {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    setTransitioning(true);
    setTimeout(() => {
      setStep((s) => s + 1);
      setTransitioning(false);
    }, 300);
  }

  function handleBack() {
    if (step > 0) {
      setTransitioning(true);
      setTimeout(() => {
        setStep((s) => s - 1);
        setTransitioning(false);
      }, 300);
    }
  }

  function handleReset() {
    setTransitioning(true);
    setTimeout(() => {
      setStep(0);
      setAnswers({ experience: null, industry: null, level: null, positions: null });
      setTransitioning(false);
    }, 300);
  }

  const result = isComplete ? getRecommendation(answers) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-stone-100">
      <ToolHero
        title="Resume Length Advisor"
        description="Answer 4 quick questions to find out if your resume should be 1 or 2 pages."
        category="Resume Formatting"
      />

      <div className="max-w-2xl mx-auto px-4 py-12">
        {/* Progress bar */}
        {!isComplete && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-600">
                Step {step + 1} of {totalSteps}
              </span>
              <span className="text-sm text-gray-400">
                {Math.round(((step + 1) / totalSteps) * 100)}%
              </span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-2 bg-blue-500 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${((step + 1) / totalSteps) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Quiz card */}
        {!isComplete && step < questions.length && (
          <div
            className={`bg-white rounded-2xl shadow-lg border border-gray-100 p-8 transition-all duration-300 ${
              transitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
            }`}
          >
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">
              {questions[step].question}
            </h2>

            <div className="grid gap-3">
              {questions[step].options.map((option) => {
                const currentAnswer = answers[questions[step].key];
                const isSelected = currentAnswer === option.value;

                return (
                  <button
                    key={option.value}
                    onClick={() => handleSelect(questions[step].key, option.value)}
                    className={`w-full text-left border rounded-xl p-4 cursor-pointer transition-all duration-200 ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500'
                        : 'border-gray-200 hover:border-blue-500 hover:bg-blue-50'
                    }`}
                  >
                    <span className="text-base font-medium text-gray-800">
                      {option.label}
                    </span>
                  </button>
                );
              })}
            </div>

            {step > 0 && (
              <button
                onClick={handleBack}
                className="mt-6 text-sm text-gray-500 hover:text-blue-600 transition-colors flex items-center gap-1"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Back
              </button>
            )}
          </div>
        )}

        {/* Results */}
        {isComplete && result && (
          <div
            className={`transition-all duration-500 ${
              transitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
            }`}
          >
            {/* Main result card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center mb-8">
              <p className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-3">
                Your Recommendation
              </p>
              <h2 className="text-4xl font-extrabold text-gray-900 mb-6">
                Your Resume Should Be{' '}
                <span className="text-blue-600">{result.recommendation}</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed text-left">
                {result.explanation}
              </p>
            </div>

            {/* Tips card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <svg
                  className="w-6 h-6 text-blue-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
                Tips for Your Resume
              </h3>
              <ul className="space-y-3">
                {result.tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-1 flex-shrink-0 w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">
                      {i + 1}
                    </span>
                    <span className="text-gray-700 leading-relaxed">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Expert Exceptions card */}
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-8 mb-8">
              <h3 className="text-xl font-bold text-blue-900 mb-3 flex items-center gap-2">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Expert Exceptions
              </h3>
              <p className="text-blue-800 leading-relaxed">{result.exceptions}</p>
            </div>

            {/* Start Over */}
            <div className="text-center mb-8">
              <button
                onClick={handleReset}
                className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Start Over
              </button>
            </div>

            {/* Educational Section */}
            <section className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mt-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">The Science Behind Resume Length</h3>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Resume length is one of the most debated topics in career advice, but research and recruiter surveys consistently point to a few clear guidelines. The right length depends on your experience level, industry norms, and how much substantive content you can offer.
                </p>
                <h4 className="text-lg font-bold text-gray-900 pt-2">What the Data Says</h4>
                <ul className="list-disc pl-5 space-y-2">
                  <li>A study by ResumeGo found that recruiters were 2.3 times more likely to prefer two-page resumes over one-page resumes for mid-level and senior candidates, contradicting the long-held one-page myth.</li>
                  <li>According to the Bureau of Labor Statistics, the median tenure for workers ages 25-34 is only 2.8 years, meaning early-career professionals rarely have enough experience to fill two pages meaningfully.</li>
                  <li>Eye-tracking research shows recruiters spend an average of 7.4 seconds on an initial resume scan. Regardless of length, information at the top of page one gets the most attention.</li>
                </ul>
                <h4 className="text-lg font-bold text-gray-900 pt-2">Industry-Specific Norms</h4>
                <ul className="list-disc pl-5 space-y-2">
                  <li><strong>Federal government:</strong> USAJobs resumes are typically 2-5 pages and require detailed descriptions of duties, accomplishments, and hours worked per week.</li>
                  <li><strong>Academia:</strong> Curriculum vitae (CVs) have no page limit and grow throughout your career, including publications, grants, and teaching history.</li>
                  <li><strong>Tech and startups:</strong> Many hiring managers still prefer concise one-page resumes, even for experienced candidates, reflecting a culture that values brevity.</li>
                  <li><strong>Executive roles:</strong> Two pages is standard for VP, C-suite, and director-level positions where breadth of leadership experience matters.</li>
                </ul>
                <h4 className="text-lg font-bold text-gray-900 pt-2">The Golden Rule</h4>
                <p>
                  The best resume is the one where every line earns its place. A tight one-page resume is always better than a padded two-page resume. If cutting to one page means removing quantified achievements that directly relate to your target role, keep the second page. If your second page is full of outdated roles, obvious skills, or filler bullet points, cut it.
                </p>
                <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
                  <strong>Sources &amp; Further Reading:</strong>{' '}
                  <a href="https://www.indeed.com/career-advice/resumes-cover-letters/resume-length" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Indeed: How Long Should a Resume Be?</a>
                  {' · '}
                  <a href="https://www.bls.gov/news.release/tenure.nr0.htm" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Bureau of Labor Statistics: Employee Tenure Summary</a>
                  {' · '}
                  <a href="https://www.careeronestop.org/JobSearch/Resumes/resume-guide.aspx" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">CareerOneStop Resume Guide (U.S. DOL)</a>
                </p>
              </div>
            </section>

            {/* CTA */}
            <ToolCTA
              headline="Get the right length AND the right content"
              description="Our AI resume builder automatically formats your resume to the perfect length while optimizing every bullet point for maximum impact."
              buttonText="Build My Perfect-Length Resume"
            />
          </div>
        )}
      </div>
    </div>
  );
}
